/**
 * Sawyer answers 10-29, item 2 — the printable "Going home today" sheet.
 *
 * The paper the desk works from at pick-up: every dog leaving today, by
 * pick-up time, with what it brought, its locker and whether food goes home,
 * plus a tick box per dog. The same `buildGoingHomeList` as the screen, from
 * the page's already-fetched `/today` payload, so the paper can never
 * disagree with the list it was printed from (Principle 1). No new fetch.
 *
 * Print mechanics copy ArrivalsBoard (#564) and RunCardPrintLayout: the sheet
 * renders through a PORTAL as a direct child of <body> marked
 * `data-print-root`, and the print stylesheet hides every other child of
 * <body>, so only the sheet prints. The screen preview keeps a sticky
 * Close / Print toolbar that print drops. Like the morning board, the sheet is
 * flat black on white (the documented print exception to the token rule).
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { DashboardArrival } from '../dashboard/dashboard-api.js';
import {
  belongingsLabel,
  buildGoingHomeList,
  foodLabel,
  pickUpLabel,
} from './going-home-list.js';
import { GoingHomeNotes } from './GoingHomeToday.js';
import './going-home.css';

export interface GoingHomeBoardProps {
  readonly facilityName: string;
  /** YYYY-MM-DD. */
  readonly date: string;
  readonly arrivals: readonly DashboardArrival[];
  readonly departures: readonly DashboardArrival[];
  readonly dataSuspect: boolean;
  readonly onClose: () => void;
}

export function GoingHomeBoard({
  facilityName,
  date,
  arrivals,
  departures,
  dataSuspect,
  onClose,
}: GoingHomeBoardProps): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = buildGoingHomeList({ arrivals, departures });
  const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const printedAt = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const showCare = list.careSent;

  const sheet = (
    <div
      className="ferro-gohome-sheet"
      data-print-root="true"
      data-testid="going-home-board"
      role="region"
      aria-label={`Going home today, ${dateLabel}`}
    >
      <div className="ferro-gohome-sheet__toolbar">
        <strong>Going home today · {dateLabel}</strong>
        <div className="ferro-gohome-sheet__actions">
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onClose} data-testid="going-home-board-close">
            Close
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => window.print()}
            data-testid="going-home-board-print"
          >
            Print
          </button>
        </div>
      </div>

      <div className="ferro-gohome-sheet__doc">
        <header className="ferro-gohome-sheet__header">
          <div>
            <p className="ferro-gohome-sheet__facility">{facilityName}</p>
            <h1 className="ferro-gohome-sheet__title">Going home today</h1>
            <p className="ferro-gohome-sheet__date">
              {dateLabel} · {list.dogCount} {list.dogCount === 1 ? 'dog' : 'dogs'}
            </p>
          </div>
          <p className="ferro-gohome-sheet__printed">Printed {printedAt}</p>
        </header>

        <GoingHomeNotes list={list} dataSuspect={dataSuspect} />

        {list.dogCount === 0 ? (
          <p className="ferro-gohome-sheet__empty" data-testid="going-home-board-empty">
            {dataSuspect ? 'No dogs loaded for today in this system yet.' : 'No dogs going home today.'}
          </p>
        ) : (
          list.blocks.map((block) => (
            <section key={block.key} className="ferro-gohome-sheet__block" data-testid={`going-home-board-block-${block.key}`}>
              <h2 className="ferro-gohome-sheet__block-title">{block.label}</h2>
              <table className="ferro-gohome-sheet__table">
                <thead>
                  <tr>
                    <th className="ferro-gohome-sheet__tick-col" aria-label="Sent home" />
                    <th>Dog</th>
                    <th>Service</th>
                    <th>Pick-up</th>
                    {showCare ? <th>Belongings</th> : null}
                    {showCare ? <th>Locker</th> : null}
                    {showCare ? <th>Food</th> : null}
                  </tr>
                </thead>
                {block.households.map((h) => (
                  <tbody key={h.key} className="ferro-gohome-sheet__household">
                    {h.rows.map((row, i) => {
                      const food = foodLabel(row);
                      return (
                        <tr key={row.key} data-testid={`going-home-board-row-${row.key}`}>
                          <td className="ferro-gohome-sheet__tick-col">
                            <span className="ferro-gohome-sheet__tick" aria-hidden="true" />
                          </td>
                          <td>
                            <span className="ferro-gohome-sheet__dog">{row.dogName}</span>
                            {i === 0 ? <span className="ferro-gohome-sheet__owner">{h.customerName}</span> : null}
                            {row.notCheckedIn ? <span className="ferro-gohome-sheet__small">Not checked in yet</span> : null}
                          </td>
                          <td data-label="Service">
                            {row.serviceLabel}
                            {row.addedServices.length > 0 ? (
                              <span className="ferro-gohome-sheet__small">+ {row.addedServices.join(', ')}</span>
                            ) : null}
                          </td>
                          <td className="ferro-gohome-sheet__num" data-label="Pick-up">{pickUpLabel(row.pickUpTime) ?? '—'}</td>
                          {showCare ? <td data-label="Belongings">{belongingsLabel(row)}</td> : null}
                          {showCare ? <td className="ferro-gohome-sheet__locker" data-label="Locker">{row.locker ?? '—'}</td> : null}
                          {showCare ? (
                            <td
                              className={food?.tone === 'send' ? 'ferro-gohome-sheet__food-send' : undefined}
                              data-label="Food"
                            >
                              {food ? food.text : '—'}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </section>
          ))
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
